import api from './api';

export interface AdminClient {
  id: string;
  /** ``null`` per un Contatto (``source: 'web'``): approvato dal sito, il
   *  cliente NTS nasce al primo ordine. */
  an_conto: number | null;
  userId?: string | null;  // FK verso ``users.userId`` — null se nessun account web
  accountEmail?: string | null;
  passwordResetAt?: string | null;  // ultimo reset credenziali avviato dall'admin
  company: string;
  vat: string;
  fiscalCode: string;
  address: string;
  city: string;
  cap: string;
  province: string;
  email: string;
  phone: string;
  mobile: string;
  status: 'attivo' | 'sospeso' | 'bloccato';
  agent: string | null;
  totalOrders: number | null;
  balance: string | number | null;
  lastOrder: string | null;
  /** ``web``: Contatto, solo sul portale (chip WEB, la riga apre la
   *  Registrazione); ``erp``: cliente del Gestionale. */
  source: 'erp' | 'web';
  contatto?: boolean;
  /** Solo per un Contatto: data della registrazione. */
  registratoIl?: string | null;
  /** Ultima azione sul portale. Popolato **solo** dal filtro
   * ``source=online``: risolvere la presenza per ogni riga di ogni pagina
   * costerebbe un join in più per una colonna quasi sempre vuota. */
  lastActivityAt?: string | null;
  // Seed flags — true se l'anagrafica è stata creata via migration come fixture di test
  seed?: boolean;
  budgetSeed?: boolean;
  seedLabel?: string | null;
}

/** ``online`` è esclusivo con gli altri tre, non si interseca: mostra i
 * Clienti con un'azione nella finestra di presenza corrente (bersaglio
 * della card "Online ora" della dashboard). */
export type AdminClientSourceFilter = 'all' | 'gestionale' | 'both' | 'web' | 'online';

export interface AdminClientListResponse {
  items: AdminClient[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface AnagraficaDestination {
  dd_coddest: number | null;
  dd_nomdest: string | null;
  inddest: string | null;
  citta: string | null;
  prov: string | null;
  cap: string | null;
  telef: string | null;
  email: string | null;
}

export interface AnagraficaDetail {
  an_conto: number;
  an_tipo: string | null;
  ragioneSociale: string;
  ragioneSocialeAlt: string | null;
  siglaRicerca: string | null;
  codiceContoMastro: number | null;
  status: string;
  statusFlag: string | null;
  sesso: string | null;
  indirizzo: string | null;
  cap: string | null;
  citta: string | null;
  provincia: string | null;
  nazione: string | null;
  email: string | null;
  pec: string | null;
  telefono: string | null;
  cellulare: string | null;
  codiceSDI: string | null;
  emailSdi: string | null;
  /** Riferimento Acquisti */
  rifAcquistiNominativo: string | null;
  /** Riferimento Amministrativo */
  rifAmministrativoNominativo: string | null;
  rifAmministrativoEmail: string | null;
  rifAmministrativoTelefono: string | null;
  /** Riferimento Tecnico (campi NTS in attesa partner) */
  rifTecnicoNominativo: string | null;
  rifTecnicoEmail: string | null;
  rifTecnicoTelefono: string | null;
  partitaIva: string | null;
  codiceFiscale: string | null;
  privato: boolean | null;
  soggettoResidente: boolean | null;
  splitPayment: boolean | null;
  bolli: boolean | null;
  // Blocco cliente
  bloccoCliente: boolean | null;            // an_blocco='S' → cliente bloccato
  dataBlocco: string | null;                // [TODO] campo NTS da identificare
  operatoreBlocco: string | null;           // [TODO] campo NTS da identificare
  dataSblocco: string | null;               // [TODO] campo NTS da identificare
  operatoreSblocco: string | null;          // [TODO] campo NTS da identificare
  listino: number | null;
  listinoLabel: string | null;
  codicePagamento: number | null;
  codicePagamentoLabel: string | null;
  codiceBanca: number | null;
  bancaDescrizione: string | null;
  bancaFiliale: string | null;
  abi: number | null;
  cab: number | null;
  iban: string | null;
  agenteCodice: number | null;
  /** Agente esterno (NTS slot 1 = an_agente) */
  agenteEsternoNome: string | null;
  agenteEsternoEmail: string | null;
  agenteEsternoTelefono: string | null;
  /** Agente interno (NTS slot 2 = an_agente2) */
  agenteInternoCodice: number | null;
  agenteInternoNome: string | null;
  agenteInternoEmail: string | null;
  agenteInternoTelefono: string | null;
  porto: string | null;
  portoLabel: string | null;
  sconto1: number | null;
  sconto2: number | null;
  /** Logistica & spedizione */
  vettoreCodice: number | null;
  /** Nome da `lookup_vettori` (dai documenti NTS), "Vettore {codice}" se ignoto */
  vettoreLabel: string | null;
  /** Flag "Vett. Pref." (`an_hhegvettpref`) */
  vettorePreferenziale: boolean | null;
  /** `an_acuradi`: "M", "D", "V" o null */
  trasportoACuraDel: string | null;
  trasportoMinimo: number | null;
  budgetEnabled: boolean;
  budgetValore: number | null;
  budgetPercentuale: number | null;
  budgetMesiPeriodo: number | null;
  budgetFatturatoCorrente: number | null;
  budgetDataInizio: string | null;
  /** Calcolato server-side = inizio + mesiPeriodo */
  budgetDataFine: string | null;
  budgetStatus: string | null;
  /** Coupon premium (placeholder finché feature non integrata) */
  budgetCoupon: string | null;
  budgetDataUltimoCoupon: string | null;
  budgetCouponUtilizzati: number | null;
  budgetValoreCoupon: number | null;
  dataApertura: string | null;
  dataInizioRapporto: string | null;
  dataFineRapporto: string | null;
  dataUltimoAggiornamento: string | null;
  destinazioni: AnagraficaDestination[];
  destinazionePreferita?: number | null;  // dd_coddest preferita dall'utente web
  fetchedAt: string | null;
  fromCache: boolean;
  seed?: boolean;
  budgetSeed?: boolean;
  seedLabel?: string | null;
  // Account web (richiesta utente 23/02/2026: Cambio Mail / Reset Credenziali / Dettaglio Accessi)
  userId?: string | null;
  accountEmail?: string | null;
  passwordResetAt?: string | null;
  /** Nome, cognome e stato del Titolare dell'account web */
  accountNome?: string | null;
  accountCognome?: string | null;
  accountAttivo?: boolean | null;
}

export interface LoginHistoryEntry {
  userId: string;
  at: string;
  ip: string | null;
}

export interface LoginHistoryResponse {
  items: LoginHistoryEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  hasNext: boolean;
  hasPrev: boolean;
  lastLogin: string | null;
  email: string | null;
}

export const adminClientService = {
  async list(opts: { q?: string; page?: number; limit?: number; source?: AdminClientSourceFilter; authToken?: string } = {}): Promise<AdminClientListResponse> {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.source) params.set('source', opts.source);
    const qs = params.toString();
    const { data } = await api.get<AdminClientListResponse>(
      `/api/admin/clients${qs ? `?${qs}` : ''}`,
      opts.authToken ? { headers: { Authorization: `Bearer ${opts.authToken}` } } : undefined,
    );
    return data;
  },

  /**
   * Dettaglio anagrafica read-only, letto da Mongo. Con ``refresh`` prima
   * l'Aggiornamento diretto del conto dal gestionale.
   */
  async getDetail(anConto: number, refresh = false): Promise<AnagraficaDetail> {
    const { data } = await api.get<AnagraficaDetail>(
      `/api/admin/clienti/${anConto}/dettaglio`,
      { params: refresh ? { refresh: 1 } : undefined },
    );
    return data;
  },

  // ── Azioni account web (richiesta utente 23/02/2026) ──────────────────
  async createAccount(anConto: number, email: string): Promise<{ message: string; userId: string; email: string }> {
    const { data } = await api.post(`/api/admin/clients/${anConto}/account`, { email });
    return data;
  },

  async changeEmail(clientKey: string | number, newEmail: string): Promise<{ message: string; email: string }> {
    const { data } = await api.patch(`/api/admin/clients/${clientKey}/email`, { newEmail });
    return data;
  },

  /** Reset Credenziali: invia al cliente il link per scegliere una nuova
   *  password (nessuna password in chiaro). `clientKey` può essere anche un
   *  `operatoreId`. */
  async resetPassword(clientKey: string | number): Promise<{ message: string }> {
    const { data } = await api.post(`/api/admin/clients/${clientKey}/reset-password`);
    return data;
  },

  async getLoginHistory(clientKey: string | number, page = 1, limit = 20): Promise<LoginHistoryResponse> {
    const { data } = await api.get<LoginHistoryResponse>(`/api/admin/clients/${clientKey}/login-history`, { params: { page, limit } });
    return data;
  },

};
